import React, {useCallback} from "react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import Button from "../common/button/Button";
import Icon from "../ui/Icon";
import RobotoRegular from "../../assets/fonts/Roboto-Regular";
import {logoBase64, numberToWords} from "../Utility";
import moment from "moment-timezone";

const timezone = "Asia/Kolkata";
const OrderInvoicePreview = ({invoice}) => {
    const generatePdf = useCallback(() => {
        if (!invoice) return;

        const doc = new jsPDF({
            orientation: "portrait",
            unit: "mm",
            format: "a4",
        });

        const pageWidth = doc.internal.pageSize.getWidth();

        doc.addFileToVFS("Roboto-Regular.ttf", RobotoRegular);
        doc.addFont("Roboto-Regular.ttf", "Roboto", "normal");

        // Add Logo
        doc.addImage(logoBase64, "PNG", 10, 10, 40, 20);

        // Title
        doc.setFontSize(18);
        doc.text("Invoice", pageWidth / 2, 20, {align: "center"});

        // Left side details
        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");
        doc.text(`Invoice No: ${invoice.orderInvoiceNo}`, 14, 45);
        doc.text(`Order Id: ${invoice.order}`, 14, 52);
        doc.text(`Payment Method: ${invoice.paymentMethod}`, 14, 59);
        doc.text(`GSTIN No: ${invoice.gstNumber}`, 14, 66);
        doc.text(
            `Date: ${moment(invoice.orderDate).tz(timezone).format("MMM DD, YYYY, hh:mm A")}`,
            14,
            73
        );

        // Right side customer info
        const rightX = pageWidth - 60;
        const customerFields = [
            {label: "Customer", value: invoice.customer?.name, y: 45},
            {label: "Phone", value: invoice.customer?.phone, y: 52},
            {label: "GSTIN No", value: invoice.customer?.gstNumber, y: 59},
        ];

        customerFields.forEach(({label, value, y}) => {
            if (value) {
                doc.text(`${label}: ${value}`, rightX, y);
            }
        });

        // Calculate totals
        const totalUnitPrice = invoice.items.reduce((sum, item) => sum + Number(item.unitPrice), 0);
        const totalAmount = invoice.items.reduce((sum, item) => sum + Number(item.amount), 0);
        const totalDiscount = invoice.items.reduce((sum, item) => sum + Number(item.discount), 0);
        const totalGst =invoice.items.reduce((sum, item) => sum + Number(item.gstPrice), 0);

        const footerRow = Array(6).fill({ content: "", styles: { halign: "left" } });

        // Set totals in specific columns
        footerRow[3] = { content: `₹ ${totalUnitPrice.toFixed(2)}`, styles: { halign: "left", fontStyle: "normal" } }; // Price
        footerRow[4] = { content: `₹ ${totalGst.toFixed(2)}`, styles: { halign: "left", fontStyle: "normal" } }; // GST
        footerRow[5] = { content: `₹ ${totalDiscount.toFixed(2)}`, styles: { halign: "left", fontStyle: "normal" } }; // Discount
        footerRow[6] = { content: `₹ ${totalAmount.toFixed(2)}`, styles: { halign: "left", fontStyle: "normal" } }; // Total

        // Table of Ordered Items
        autoTable(doc, {
            startY: 85,
            head: [["S.No.", "Item", "Qty", "Price","GST", "Discount", "Total"]],
            body: invoice.items.map((item, i) => [
                i + 1,
                item.name,
                item.quantity,
                item.unitPrice,
                item.gstPrice.toFixed(2),
                item.discount.toFixed(2),
                item.amount,
            ]),
            theme: "grid",
            headStyles: {fillColor: [255, 204, 204], textColor: 0, fontSize: 9},
            // Footer row
            foot: [footerRow],
            footStyles: {
                fillColor: [240, 240, 240], font: "Roboto",
                fontStyle: "normal", textColor: 0, fontSize: 9
            },
        });

        // Totals
        let finalY = doc.lastAutoTable.finalY || 75;

        doc.setFont("Roboto", "normal");
        doc.setFontSize(10);
        doc.setTextColor(0, 0, 0);

        const rightMargin = pageWidth - 14;
        doc.text(`Subtotal: ₹ ${invoice.totals?.subtotal || 0} /-`, rightMargin, finalY + 10, {align: "right"});

        doc.setTextColor(255, 99, 132);
        doc.text(`Discount: - ₹ ${invoice.totals?.discount.toFixed(2) || 0} /-`, rightMargin, finalY + 16, {align: "right"});

        //GST Details
        const gstAmount = Number(invoice.totals?.gstAmount ?? 0);
        const sgst = (gstAmount / 2).toFixed(2);
        const cgst = (gstAmount / 2).toFixed(2);

        doc.text(`SGST: ₹ ${sgst} /-`, rightMargin, finalY + 22, { align: "right" });
        doc.text(`CGST: ₹ ${cgst} /-`, rightMargin, finalY + 28, { align: "right" });

        // Reset back to normal (black)
        doc.setTextColor(0, 0, 0);
        // Line above Grand Total
        doc.setDrawColor(0);
        doc.setLineWidth(0.3);
        doc.line(pageWidth - 80, finalY + 32, rightMargin, finalY + 32);

        // Grand Total
        doc.setFontSize(10);
        doc.text(`Grand Total: ₹ ${invoice.totals?.total || 0} /-`, rightMargin, finalY + 36, {align: "right"});


        //Grand Total
        doc.setFontSize(10);
        doc.setFont("Roboto", "normal");
        doc.text(`GRAND TOTAL (in Figures) ₹ :${invoice.totals?.total.toFixed(2) || "0.00"} /-`, 14, finalY + 50);
        doc.text(`GRAND TOTAL (in Words): ${numberToWords(invoice.totals?.total.toFixed(2) || "0.00")}`, 14, finalY + 55);

        doc.save(`${invoice.customer?.name}-${invoice.invoiceNo || "download"}.pdf`);
    }, [invoice]);

    if (!invoice) return null;

    return (
        <>
            <div className="mb-3 position-absolute end-0 m-2 bottom-0">
                <Button className="rounded-pill p-2" onClick={generatePdf}>
                    <Icon name="printPreview" width={24} height={24}></Icon>
                </Button>
            </div>
        </>
    );
};

export default OrderInvoicePreview;
